/**
 * CitationChip — inline button that opens a source in the right pane.
 *
 * Renders as a small pill with a Lucide icon, a short kind label, and the
 * citation's `label` text. Real <button> so it's keyboard-focusable and
 * screen-reader-friendly. Active state when the chip's source is the
 * currently focused tab.
 */

import { memo } from 'react'
import {
  ClipboardList,
  FileText,
  Landmark,
  Mail,
  PackageCheck,
  Receipt,
  Ruler,
  Table,
  type LucideIcon,
} from 'lucide-react'
import { SOURCE_KIND_META, type Citation, type SourceKind } from '../../types/cards'
import { useSourcePane } from '../../contexts/SourcePaneContext'

const ICONS: Record<string, LucideIcon> = {
  ClipboardList,
  FileText,
  Landmark,
  Mail,
  PackageCheck,
  Receipt,
  Ruler,
  Table,
}

function iconFor(kind: SourceKind): LucideIcon {
  const name = SOURCE_KIND_META[kind].icon
  return ICONS[name] ?? FileText
}

export interface CitationChipProps {
  citation: Citation
}

function CitationChipImpl({ citation }: CitationChipProps) {
  const { activeSourceId, openSource } = useSourcePane()
  const Icon = iconFor(citation.kind)
  const kindLabel = SOURCE_KIND_META[citation.kind].label
  const isActive = activeSourceId === citation.id

  return (
    <button
      type="button"
      onClick={() => openSource(citation)}
      aria-pressed={isActive}
      aria-label={`Open ${kindLabel}: ${citation.label}`}
      title={`${kindLabel} · ${citation.label}`}
      className={
        'inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-md text-xs font-medium ' +
        'border transition-colors duration-100 touch-manipulation ' +
        (isActive
          ? 'bg-amtrend-blue/10 dark:bg-amtrend-blue/20 border-amtrend-blue/60 text-amtrend-blue dark:text-blue-300'
          : 'bg-gray-50 dark:bg-surface-dark-overlay border-gray-200 dark:border-gray-700 ' +
            'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 ' +
            'hover:border-gray-300 dark:hover:border-gray-600')
      }
    >
      <Icon size={12} className="flex-shrink-0" aria-hidden="true" />
      <span className="text-[10px] uppercase tracking-wide font-semibold opacity-70 flex-shrink-0">
        {kindLabel}
      </span>
      <span className="truncate max-w-[18ch]">{citation.label}</span>
    </button>
  )
}

export const CitationChip = memo(CitationChipImpl)

// ---------------------------------------------------------------------------
// CitationChipRow — convenience wrapper that renders 0..N chips in a row
// with a leading "Sources" label, or nothing when the list is empty.
// ---------------------------------------------------------------------------

export function CitationChipRow({ citations }: { citations: Citation[] }) {
  if (!citations || citations.length === 0) return null
  return (
    <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 dark:text-gray-500 mr-1">
        Sources
      </span>
      {citations.map(c => (
        <CitationChip key={c.id} citation={c} />
      ))}
    </div>
  )
}

export default CitationChip
